import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCart } from '../../../Context/CartContext';
import AuthContext from '../../../Context/AuthContext';
import { couponAPI, ordersAPI } from '../../../Api/api';
import { useAppLanguage } from '../../../Hooks/useAppLanguage';
import { useToast } from '../../../Context/ToastContext';
import { usePublicSettings } from '../../../Context/PublicSettingsContext';
import Button from '../../../Component/UI/Button/Button';
import { SHIPPING_HANDLING } from '../../../Utils/orderConfig';
import { rememberPendingOnlinePayment } from '../../../Utils/pendingOnlinePayment';
import { roundMoney } from '../../../Utils/currencyFormat';
import CheckoutFlowHeader from '../CheckoutFlowHeader';
import './Checkout.css';

/** Payment options, each shown only while the merchant has its setting turned on. */
const PAYMENT_OPTIONS = [
    { value: 'cod', setting: 'cashOnDelivery', labelKey: 'checkout.codTitle' },
    { value: 'online', setting: 'online', labelKey: 'checkout.onlinePaymentTitle' },
    { value: 'ibanTransfer', setting: 'bankTransfer', labelKey: 'checkout.ibanTitle' },
];

// What the storefront offered before stores had payment settings.
const DEFAULT_PAYMENT_METHODS = { cashOnDelivery: true, bankTransfer: true, online: false };

/** Mirrors the server's preview maths so the summary agrees with the placed order. */
const resolveDiscountAmount = (appliedDiscount, subtotal) => {
    if (!appliedDiscount || appliedDiscount.code === 'None') return 0;
    if (appliedDiscount.discountPercentage > 0) {
        return (subtotal * appliedDiscount.discountPercentage) / 100;
    }
    return Math.min(subtotal, Number(appliedDiscount.discountAmount) || 0);
};

const Checkout = () => {
    const { userId } = useParams();
    const { t, langPath, dir } = useAppLanguage();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const { cartItems, getCartTotal, clearCart } = useCart();
    const { addToast } = useToast();
    const { formatPrice, publicSettings, currencyCode } = usePublicSettings();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('cod');

    const enabledMethods = publicSettings?.paymentMethods || DEFAULT_PAYMENT_METHODS;
    const paymentOptions = PAYMENT_OPTIONS.filter((option) => enabledMethods[option.setting]);
    const paymentOptionAvailable = paymentOptions.some((option) => option.value === paymentMethod);
    const firstPaymentOption = paymentOptions[0]?.value;

    // The merchant may have turned the selected method off; move to one that is offered.
    useEffect(() => {
        if (!paymentOptionAvailable && firstPaymentOption) {
            setPaymentMethod(firstPaymentOption);
        }
    }, [paymentOptionAvailable, firstPaymentOption]);
    const [couponCode, setCouponCode] = useState('');
    const [couponStatus, setCouponStatus] = useState('idle');
    const [couponError, setCouponError] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [form, setForm] = useState({
        fullName: '',
        email: '',
        phone: '',
        shippingAddress: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        billingAddress: '',
    });

    const subtotal = getCartTotal();
    // Display only — the server re-validates the code and recomputes these on submit.
    const discountAmount = resolveDiscountAmount(appliedCoupon?.appliedDiscount, subtotal);
    const taxableBase = Math.max(0, subtotal - discountAmount) + SHIPPING_HANDLING;
    const taxRate = Math.min(100, Math.max(0, Number(publicSettings?.taxRatePercent) || 0));
    // Inclusive (Saudi VAT): the tax is already inside the prices, so it is shown, not added.
    const pricesIncludeTax = Boolean(publicSettings?.pricesIncludeTax);
    const tax = pricesIncludeTax
        ? roundMoney(taxableBase - taxableBase / (1 + taxRate / 100), currencyCode)
        : roundMoney(taxableBase * (taxRate / 100), currencyCode);
    const total = pricesIncludeTax ? taxableBase : taxableBase + tax;

    const handleApplyCoupon = async () => {
        const code = couponCode.trim();
        if (!code) return;

        setCouponStatus('loading');
        setCouponError('');
        try {
            const response = await couponAPI.validate({ code, subtotal });
            setAppliedCoupon(response.data);
            setCouponStatus('applied');
        } catch (error) {
            setAppliedCoupon(null);
            setCouponStatus('error');
            setCouponError(error?.response?.data?.message || t('checkout.couponFailed'));
        }
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponCode('');
        setCouponStatus('idle');
        setCouponError('');
    };

    useEffect(() => {
        if (!cartItems.length) {
            addToast(t('checkout.emptyCart'), 'info');
            navigate(langPath('/cart'), { replace: true });
        }
    }, [addToast, cartItems.length, langPath, navigate, t]);

    useEffect(() => {
        if (user?.name || user?.email) {
            setForm((prev) => ({
                ...prev,
                fullName: prev.fullName || user.name || '',
                email: prev.email || user.email || '',
            }));
        }
    }, [user?.email, user?.name]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const buildOrderItems = () =>
        cartItems.map((item) => ({
            productId: item.productId,
            name: item.name,
            description: item.description || item.name,
            quantity: item.quantity,
            price: item.price,
            category: item.category,
            subCategory: item.subCategory,
            primaryImage: item.imageUrl,
            selectedOptions: Array.isArray(item.selectedOptions) ? item.selectedOptions : [],
            // Legacy fields for older backends / display fallbacks
            size: item.size,
            color: item.color,
            selectedSize: item.size,
            selectedColor: item.color,
        }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!cartItems.length) {
            addToast(t('cart.empty'), 'error');
            navigate(langPath('/cart'));
            return;
        }
        if (!paymentOptionAvailable) {
            addToast(t('checkout.noPaymentMethods'), 'error');
            return;
        }

        setIsSubmitting(true);
        let leavingForPayment = false;
        try {
            const orderData = {
                userId,
                personalInfo: {
                    fullName: form.fullName,
                    email: form.email,
                    phone: form.phone,
                    shippingAddress: form.shippingAddress,
                    city: form.city,
                    state: form.state,
                    postalCode: form.zip,
                    country: form.country,
                    billingAddress: form.billingAddress || form.shippingAddress,
                },
                terms: true,
                items: buildOrderItems(),
                ...(appliedCoupon?.coupon?.code ? { coupon: { code: appliedCoupon.coupon.code } } : {}),
                shippingHandling: SHIPPING_HANDLING,
                paymentDetails: {},
            };

            if (paymentMethod === 'online') {
                const response = await ordersAPI.createOnline(orderData, langPath('/order-confirmation'));
                const redirectUrl = response?.data?.checkout?.redirectUrl;
                if (!redirectUrl) {
                    addToast(t('checkout.onlinePaymentUnavailable'), 'error');
                    return;
                }
                // The cart stays until the payment is confirmed, so a failed payment can be retried.
                rememberPendingOnlinePayment(response?.data?.data?.orderNumber);
                leavingForPayment = true;
                window.location.assign(redirectUrl);
                return;
            }

            const response = paymentMethod === 'ibanTransfer'
                ? await ordersAPI.createIbanTransfer(orderData)
                : await ordersAPI.createCashOnDelivery(orderData);

            clearCart();
            const payload = response?.data?.data || response?.data;
            const orderNumber = payload?.orderNumber;
            if (orderNumber) {
                navigate(langPath(`/order-confirmation?orderNumber=${encodeURIComponent(orderNumber)}`));
            } else {
                navigate(langPath('/order-confirmation'));
            }
        } catch (err) {
            addToast(err?.response?.data?.message || t('checkout.orderFailed'), 'error');
        } finally {
            // Keep the button disabled while the browser leaves for the payment page.
            if (!leavingForPayment) setIsSubmitting(false);
        }
    };

    if (!cartItems.length) {
        return null;
    }

    return (
        <main className="checkout-flow-main checkout-page">
            <CheckoutFlowHeader />
            <div className="container" dir={dir}>
                <h1 className="checkout-title">{t('checkout.title')}</h1>

                <form className="checkout-form" onSubmit={handleSubmit}>
                    <fieldset className="checkout-fieldset">
                        <legend>{t('checkout.personalDetails')}</legend>
                        <div className="checkout-grid">
                            <label>
                                {t('checkout.fullName')}
                                <input name="fullName" value={form.fullName} onChange={handleChange} required />
                            </label>
                            <label>
                                {t('checkout.email')}
                                <input type="email" name="email" value={form.email} onChange={handleChange} required />
                            </label>
                            <label>
                                {t('checkout.phone')}
                                <input name="phone" value={form.phone} onChange={handleChange} required />
                            </label>
                        </div>
                    </fieldset>

                    <fieldset className="checkout-fieldset">
                        <legend>{t('checkout.shippingLegend')}</legend>
                        <div className="checkout-grid">
                            <label className="checkout-full">
                                {t('checkout.shippingAddress')}
                                <input name="shippingAddress" value={form.shippingAddress} onChange={handleChange} required />
                            </label>
                            <label>
                                {t('checkout.city')}
                                <input name="city" value={form.city} onChange={handleChange} required />
                            </label>
                            <label>
                                {t('checkout.state')}
                                <input name="state" value={form.state} onChange={handleChange} required />
                            </label>
                            <label>
                                {t('checkout.zip')}
                                <input name="zip" value={form.zip} onChange={handleChange} required />
                            </label>
                            <label>
                                {t('checkout.country')}
                                <input name="country" value={form.country} onChange={handleChange} required />
                            </label>
                        </div>
                    </fieldset>

                    <fieldset className="checkout-fieldset">
                        <legend>{t('checkout.billingInfo')}</legend>
                        <label className="checkout-full">
                            {t('checkout.billingAddress')}
                            <input
                                name="billingAddress"
                                value={form.billingAddress}
                                onChange={handleChange}
                                placeholder={form.shippingAddress}
                            />
                        </label>
                    </fieldset>

                    <fieldset className="checkout-fieldset">
                        <legend>{t('checkout.selectPaymentMethodLegend')}</legend>
                        {paymentOptions.length === 0 ? (
                            <p className="checkout-payment-note" role="alert">{t('checkout.noPaymentMethods')}</p>
                        ) : (
                            paymentOptions.map((option) => (
                                <label key={option.value} className="checkout-radio">
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value={option.value}
                                        checked={paymentMethod === option.value}
                                        onChange={() => setPaymentMethod(option.value)}
                                    />
                                    {t(option.labelKey)}
                                </label>
                            ))
                        )}
                        {paymentMethod === 'online' && paymentOptionAvailable ? (
                            <p className="checkout-payment-note">{t('checkout.onlinePaymentHint')}</p>
                        ) : null}
                    </fieldset>

                    <aside className="checkout-summary">
                        <h2>{t('checkout.orderSummary')}</h2>
                        <p>{t('checkout.items')} {cartItems.length}</p>
                        <p>{t('cart.subtotal')} {formatPrice(subtotal)}</p>
                        {discountAmount > 0 && (
                            <p className="checkout-discount">
                                {t('checkout.discount')} -{formatPrice(discountAmount)}
                            </p>
                        )}
                        <p>{t('checkout.shippingHandling')} {formatPrice(SHIPPING_HANDLING)}</p>
                        {tax > 0 && <p>{t(pricesIncludeTax ? 'checkout.taxIncluded' : 'checkout.tax')} {formatPrice(tax)}</p>}
                        <p className="checkout-total">{t('checkout.orderTotal')} {formatPrice(total)}</p>

                        <div className="checkout-coupon">
                            <label className="checkout-coupon-label" htmlFor="checkout-coupon">
                                {t('checkout.couponTitle')}
                            </label>
                            {appliedCoupon ? (
                                <div className="checkout-coupon-applied">
                                    <span>
                                        {t('checkout.couponApplied', { code: appliedCoupon.coupon?.code })}
                                    </span>
                                    <Button
                                        type="button"
                                        variant="link"
                                        size="sm"
                                        onClick={handleRemoveCoupon}
                                    >
                                        {t('checkout.removeCoupon')}
                                    </Button>
                                </div>
                            ) : (
                                <div className="checkout-coupon-row">
                                    <input
                                        id="checkout-coupon"
                                        type="text"
                                        className="checkout-coupon-input"
                                        placeholder={t('checkout.couponPlaceholder')}
                                        value={couponCode}
                                        onChange={(event) => setCouponCode(event.target.value)}
                                        disabled={couponStatus === 'loading'}
                                        autoComplete="off"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={handleApplyCoupon}
                                        disabled={couponStatus === 'loading' || !couponCode.trim()}
                                    >
                                        {couponStatus === 'loading'
                                            ? t('checkout.applyingCoupon')
                                            : t('checkout.applyCoupon')}
                                    </Button>
                                </div>
                            )}
                            {couponStatus === 'error' && couponError ? (
                                <p className="checkout-coupon-error" role="alert">
                                    {couponError}
                                </p>
                            ) : null}
                        </div>
                    </aside>

                    <div className="checkout-actions">
                        <Button type="button" variant="outline" onClick={() => navigate(langPath('/cart'))}>
                            {t('checkout.goBack')}
                        </Button>
                        <Button type="submit" disabled={isSubmitting || !paymentOptionAvailable}>
                            {isSubmitting
                                ? t('checkout.placingOrder')
                                : paymentMethod === 'online'
                                    ? t('checkout.continueToPayment')
                                    : t('checkout.confirmOrder')}
                        </Button>
                    </div>
                </form>
            </div>
        </main>
    );
};

export default Checkout;
